import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth, formatApiError } from "../context/AuthContext";
import Seo from "../components/Seo";
import { Sparkles, Upload } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";

const fmtEur = (n, language) => new Intl.NumberFormat(language === "en" ? "en-IE" : "it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(parseFloat(n) || 0);

export default function Sell() {
  const { language, t } = useLanguage();
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [config, setConfig] = useState({ platform_fee_percent: 10 });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [galleryUrls, setGalleryUrls] = useState([""]);
  const [form, setForm] = useState({
    title: "", description: "", category: "", starting_bid: "", condition: "Ottimo",
    provenance: "", estimated_low: "", estimated_high: "", image_url: "", ends_in_hours: 168,
    shipping_cost: "0", shipping_notes: "",
  });

  useEffect(() => { if (ready && (!user || user === false)) nav("/login"); }, [ready, user, nav]);
  useEffect(() => {
    axios.get(`${API}/categories`).then(r => setCats(r.data));
    axios.get(`${API}/config`).then(r => setConfig(r.data));
  }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const runAI = async () => {
    if (!file) { toast.error(t("Carica prima una foto")); return; }
    setAnalyzing(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      fd.append("category", form.category);
      fd.append("hint", form.description || "");
      const { data } = await axios.post(`${API}/ai/analyze-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm(f => ({
        ...f,
        title: data.title || f.title,
        description: data.description || f.description,
        condition: data.condition || f.condition,
        estimated_low: data.estimated_low || f.estimated_low,
        estimated_high: data.estimated_high || f.estimated_high,
        starting_bid: data.starting_bid || f.starting_bid,
      }));
      toast.success(t("Scheda generata dall'AI"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("AI non disponibile"));
    } finally {
      setAnalyzing(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_url && !preview) { toast.error(t("Serve una URL immagine o carica una foto (per demo inserisci URL)")); return; }
    if (!acceptedTerms) { toast.error(`${t("Devi accettare la commissione del ")}${config.platform_fee_percent}${t("% per pubblicare")}`); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        starting_bid: parseFloat(form.starting_bid),
        estimated_low: form.estimated_low ? parseFloat(form.estimated_low) : null,
        estimated_high: form.estimated_high ? parseFloat(form.estimated_high) : null,
        image_url: form.image_url || preview,
        gallery_images: galleryUrls.map(u => u.trim()).filter(Boolean),
        ends_in_hours: parseInt(form.ends_in_hours),
        shipping_cost: parseFloat(form.shipping_cost || 0),
        shipping_notes: form.shipping_notes || null,
      };
      const { data } = await axios.post(`${API}/auctions`, payload);
      toast.success(t("Lotto pubblicato"));
      nav(`/auction/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  const upd = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12">
      <Seo
        title="Vendi con Rarium — AI Assist per Descrizioni e Stime"
        description="Metti in asta il tuo pezzo su Rarium. La nostra AI genera scheda editoriale e stima di mercato in secondi. Commissioni trasparenti, spedizione assicurata."
        path="/sell"
      />
      <div className="overline text-stone-500">{t("Vendi con Rarium")}</div>
      <h1 className="font-serif text-5xl sm:text-6xl tracking-tighter mt-2">{t("Metti in asta il tuo pezzo.")}</h1>
      <p className="mt-4 text-stone-600 max-w-2xl">{t("La nostra AI ti aiuta a scrivere una scheda editoriale e a stimare il valore in secondi. Approva, modifica e pubblica.")}</p>

      {/* Commissione trasparente */}
      <div className="mt-8 max-w-3xl bg-[#8C3A3A]/5 border-l-4 border-[#8C3A3A] p-6">
        <div className="overline text-[#8C3A3A]">{t("Commissione Rarium")}</div>
        <div className="font-serif text-2xl mt-1">{t("Rarium trattiene il ")}{config.platform_fee_percent}{t(" sul prezzo di aggiudicazione.")}</div>
        <p className="mt-3 text-sm text-stone-700 leading-relaxed">
          {t("Se il tuo lotto viene aggiudicato a ")}<strong>{fmtEur(1000, language)}</strong>{t(", Rarium trattiene ")}<strong>{fmtEur(Math.round(1000 * (config.platform_fee_percent/100)), language)}</strong>{t(" e tu ricevi ")}<strong>{fmtEur(1000 - Math.round(1000 * (config.platform_fee_percent/100)), language)}</strong>{t(" netti tramite bonifico entro 5 giorni lavorativi dalla conferma del pagamento. La commissione copre autenticazione, marketing, gestione pagamenti e assistenza clienti. Nessun costo se il lotto non viene aggiudicato.")}
        </p>
      </div>

      <form onSubmit={submit} className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="overline text-stone-500 mb-3">{t("Foto principale")}</div>
          <label htmlFor="file" className="block aspect-[4/5] bg-stone-100 border border-stone-200 border-dashed cursor-pointer relative overflow-hidden">
            {preview ? (
              <img src={preview} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center text-stone-500">
                <Upload size={32} />
                <div className="mt-3 text-sm">{t("Clicca per caricare")}</div>
                <div className="text-xs mt-1">JPG / PNG · max 10MB</div>
              </div>
            )}
            <input data-testid="upload-input" id="file" type="file" accept="image/*" className="hidden" onChange={onFile} />
          </label>
          <button
            type="button"
            data-testid="ai-analyze-btn"
            onClick={runAI}
            disabled={analyzing || !file}
            className="mt-4 w-full text-xs uppercase tracking-widest font-semibold border border-[#8C3A3A] text-[#8C3A3A] hover:bg-[#8C3A3A] hover:text-stone-50 px-6 py-3 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Sparkles size={14} />
            {analyzing ? t("Analisi in corso…") : t("Genera scheda con AI")}
          </button>
          <div className="mt-3 text-xs text-stone-500">Powered by Gemini + Claude Sonnet 5</div>

          <div className="mt-6">
            <label className="flex flex-col">
              <span className="overline text-stone-500 mb-2">{t("Oppure URL immagine")}</span>
              <input data-testid="image-url" value={form.image_url} onChange={upd("image_url")} placeholder="https://…" className="border-b border-stone-300 bg-transparent py-2 focus:border-stone-900 focus:outline-none" />
            </label>
          </div>
        </div>

        <div className="lg:col-span-7 flex flex-col gap-6">
          <label className="flex flex-col">
            <span className="overline text-stone-500 mb-2">{t("Titolo")}</span>
            <input data-testid="sell-title" required value={form.title} onChange={upd("title")} className="border-b border-stone-300 bg-transparent py-3 focus:border-stone-900 focus:outline-none" />
          </label>
          <label className="flex flex-col">
            <span className="overline text-stone-500 mb-2">{t("Descrizione")}</span>
            <textarea data-testid="sell-description" required value={form.description} onChange={upd("description")} rows={5} className="border border-stone-300 bg-white p-3 focus:border-stone-900 focus:outline-none" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="flex flex-col">
              <span className="overline text-stone-500 mb-2">{t("Categoria")}</span>
              <select data-testid="sell-category" required value={form.category} onChange={upd("category")} className="border border-stone-300 bg-white p-3">
                <option value="">{t("Seleziona…")}</option>
                {cats.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </label>
            <label className="flex flex-col">
              <span className="overline text-stone-500 mb-2">{t("Condizioni")}</span>
              <select data-testid="sell-condition" value={form.condition} onChange={upd("condition")} className="border border-stone-300 bg-white p-3">
                <option value="Eccellente">{t("Eccellente")}</option><option value="Ottimo">{t("Ottimo")}</option><option value="Buono">{t("Buono")}</option><option value="Discreto">{t("Discreto")}</option>
              </select>
            </label>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <label className="flex flex-col">
              <span className="overline text-stone-500 mb-2">{t("Base d'asta (€)")}</span>
              <input data-testid="sell-starting-bid" type="number" required value={form.starting_bid} onChange={upd("starting_bid")} className="border-b border-stone-300 bg-transparent py-2" />
            </label>
            <label className="flex flex-col">
              <span className="overline text-stone-500 mb-2">{t("Stima min (€)")}</span>
              <input data-testid="sell-est-low" type="number" value={form.estimated_low} onChange={upd("estimated_low")} className="border-b border-stone-300 bg-transparent py-2" />
            </label>
            <label className="flex flex-col">
              <span className="overline text-stone-500 mb-2">{t("Stima max (€)")}</span>
              <input data-testid="sell-est-high" type="number" value={form.estimated_high} onChange={upd("estimated_high")} className="border-b border-stone-300 bg-transparent py-2" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <label className="flex flex-col">
              <span className="overline text-stone-500 mb-2">{t("Provenienza")}</span>
              <input data-testid="sell-provenance" value={form.provenance} onChange={upd("provenance")} className="border-b border-stone-300 bg-transparent py-2" />
            </label>
            <label className="flex flex-col">
              <span className="overline text-stone-500 mb-2">{t("Durata asta (ore)")}</span>
              <input data-testid="sell-duration" type="number" min={1} max={720} value={form.ends_in_hours} onChange={upd("ends_in_hours")} className="border-b border-stone-300 bg-transparent py-2" />
            </label>
          </div>

          {/* Spese di spedizione */}
          <div className="border-l-4 border-stone-900 bg-stone-100 p-5">
            <div className="overline text-stone-700">{t("Spese di spedizione")}</div>
            <div className="mt-4 grid grid-cols-3 gap-4">
              <label className="flex flex-col col-span-1">
                <span className="text-xs text-stone-500 mb-2">{t("Costo (€)")}</span>
                <input
                  data-testid="sell-shipping-cost"
                  type="number"
                  min="0"
                  step="1"
                  value={form.shipping_cost}
                  onChange={upd("shipping_cost")}
                  className="border-b border-stone-400 bg-transparent py-2 font-mono"
                  placeholder="0"
                />
              </label>
              <label className="flex flex-col col-span-2">
                <span className="text-xs text-stone-500 mb-2">{t("Note (facoltative)")}</span>
                <input
                  data-testid="sell-shipping-notes"
                  value={form.shipping_notes}
                  onChange={upd("shipping_notes")}
                  placeholder={t("Es. Spedizione assicurata corriere espresso, Italia")}
                  className="border-b border-stone-400 bg-transparent py-2"
                  maxLength={300}
                />
              </label>
            </div>
            <div className="mt-3 text-xs text-stone-500">
              {t("Metti 0 se la spedizione è inclusa nel prezzo. Il costo viene sommato all'aggiudicazione al checkout.")}
            </div>
          </div>

          {/* Galleria foto aggiuntive */}
          <div className="border border-stone-200 p-5 bg-white">
            <div className="flex justify-between items-center mb-3">
              <div className="overline text-stone-700">{t("Galleria foto aggiuntive")}</div>
              <button
                type="button"
                data-testid="sell-add-gallery-image"
                onClick={() => setGalleryUrls([...galleryUrls, ""])}
                className="text-xs uppercase tracking-widest font-semibold border border-stone-900 hover:bg-stone-900 hover:text-stone-50 px-3 py-2 transition-colors"
              >{t("+ Aggiungi")}</button>
            </div>
            <div className="text-xs text-stone-500 mb-4">
              {t("Inserisci URL di foto aggiuntive (dettagli, retro, imperfezioni, certificati). Aiutano gli offerenti a valutare meglio il lotto.")}
            </div>
            <div className="flex flex-col gap-3">
              {galleryUrls.map((url, idx) => (
                <div key={idx} className="flex gap-3 items-center">
                  <input
                    data-testid={`sell-gallery-url-${idx}`}
                    value={url}
                    onChange={(e) => {
                      const next = [...galleryUrls];
                      next[idx] = e.target.value;
                      setGalleryUrls(next);
                    }}
                    placeholder="https://…"
                    className="flex-1 border-b border-stone-300 bg-transparent py-2 text-sm"
                  />
                  {url && (
                    <img src={url} alt="" className="w-12 h-12 object-cover border border-stone-200" onError={(e) => e.target.style.display = 'none'} />
                  )}
                  {galleryUrls.length > 1 && (
                    <button
                      type="button"
                      data-testid={`sell-gallery-remove-${idx}`}
                      onClick={() => setGalleryUrls(galleryUrls.filter((_, i) => i !== idx))}
                      className="text-stone-500 hover:text-[#8C3A3A] text-xl leading-none"
                      aria-label={t("Rimuovi")}
                    >×</button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <button data-testid="sell-submit" type="submit" disabled={submitting || !acceptedTerms} className="mt-6 text-xs uppercase tracking-widest font-semibold bg-stone-900 hover:bg-[#8C3A3A] text-stone-50 px-8 py-5 transition-colors disabled:opacity-50">
            {submitting ? "…" : t("Pubblica il lotto")}
          </button>

          {/* Fee preview + T&C checkbox */}
          {form.starting_bid && (
            <div className="mt-2 p-4 bg-stone-100 border border-stone-200 text-sm">
              <div className="flex justify-between"><span className="text-stone-600">{t("Se aggiudicato a base d'asta (")}{fmtEur(form.starting_bid, language)})</span></div>
              <div className="flex justify-between mt-2 border-t border-stone-200 pt-2"><span className="text-stone-500">{t("Commissione Rarium")} ({config.platform_fee_percent}%)</span><span className="font-mono text-[#8C3A3A]">− {fmtEur(parseFloat(form.starting_bid) * config.platform_fee_percent / 100, language)}</span></div>
              <div className="flex justify-between mt-1 font-semibold"><span>{t("Tu ricevi (netti)")}</span><span className="font-mono">{fmtEur(parseFloat(form.starting_bid) * (1 - config.platform_fee_percent / 100), language)}</span></div>
            </div>
          )}

          <label className="mt-2 flex items-start gap-3 cursor-pointer text-sm">
            <input
              data-testid="accept-fee-checkbox"
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1"
              required
            />
            <span className="text-stone-700 leading-relaxed">
              {t("Accetto che ")}<strong>{t("Rarium tratterrà il ")}{config.platform_fee_percent}%</strong>{t(" sul prezzo di aggiudicazione come commissione di servizio. Confermo di essere proprietario dell'oggetto e ne garantisco l'autenticità e la provenienza.")}
            </span>
          </label>
        </div>
      </form>
    </div>
  );
}
